/**
 * InputPassword Component - Controller
 * ====================================
 */

import type { IScope, ISCEService, INgModelController, ITranscludeFunction } from 'angular';
import { SIZES, BASE_CLASSES, ICONS, DEFAULTS, InputPasswordSize } from './constants';

export interface InputPasswordBindings {
  label?: string;
  placeholder?: string;
  helpMessage?: string;
  error?: boolean;
  disabled?: boolean;
  size?: InputPasswordSize;
  inputId?: string;
  autocomplete?: string;
  onChange?: (args: { value: string }) => void;
}

export class InputPasswordController implements InputPasswordBindings {
  static $inject = ['$scope', '$sce', '$transclude'];

  private $scope: IScope | null;
  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  ngModelCtrl?: INgModelController | null;

  label?: string;
  placeholder?: string;
  helpMessage?: string;
  error?: boolean;
  disabled?: boolean;
  size?: InputPasswordSize;
  inputId?: string;
  autocomplete?: string;
  onChange?: (args: { value: string }) => void;

  inputValue = '';
  visible = false;
  trustedEye: unknown = null;
  trustedEyeOff: unknown = null;
  resolvedId = '';
  helpId = '';

  // Slots nombrados (label/help): detectados con isSlotFilled, que SÍ funciona
  // para multi-slot transclusion (a diferencia del slot default booleano).
  hasLabelSlot = false;
  hasHelpSlot = false;

  constructor($scope: IScope, $sce: ISCEService, $transclude: ITranscludeFunction) {
    this.$scope = $scope;
    this.$sce = $sce;
    this.$transclude = $transclude;
  }

  $onInit(): void {
    this.autocomplete = this.autocomplete || 'current-password';
    this.size = this.size || DEFAULTS.size;
    const uid = this.$scope ? String(this.$scope.$id) : Math.random().toString(36).slice(2);
    this.resolvedId = this.inputId || ('ec-password-' + uid);
    this.helpId = 'ec-password-help-' + uid;

    if (this.$transclude) {
      this.hasLabelSlot = this.$transclude.isSlotFilled('labelSlot');
      this.hasHelpSlot = this.$transclude.isSlotFilled('helpSlot');
    }

    if (this.$sce) {
      this.trustedEye = this.$sce.trustAsHtml(ICONS.eye);
      this.trustedEyeOff = this.$sce.trustAsHtml(ICONS.eyeOff);
    }

    if (this.ngModelCtrl) {
      this.ngModelCtrl.$render = () => {
        const v = this.ngModelCtrl ? this.ngModelCtrl.$viewValue : null;
        this.inputValue = v != null ? String(v) : '';
      };
    }
  }

  $onDestroy(): void {
    this.$scope = null;
    this.$sce = null;
    this.$transclude = null;
    this.ngModelCtrl = null;
  }

  handleInput(): void {
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$setViewValue(this.inputValue);
    }
    if (typeof this.onChange === 'function') {
      this.onChange({ value: this.inputValue });
    }
  }

  toggleVisibility(): void {
    if (this.disabled) return;
    this.visible = !this.visible;
  }

  getControlClasses(): string[] {
    const size = SIZES[this.size || DEFAULTS.size];
    const classes = [BASE_CLASSES.control, size.control, size.text];
    if (this.disabled) classes.push(BASE_CLASSES.controlDisabled);
    return classes;
  }

  // Figma no pinta borde en error: el color va en el texto del valor y del help.
  getInputClasses(): string[] {
    const classes = [BASE_CLASSES.input];
    if (this.disabled) {
      classes.push(BASE_CLASSES.inputDisabled);
    } else if (this.error) {
      classes.push(BASE_CLASSES.inputError);
    } else {
      classes.push(BASE_CLASSES.inputColor);
    }
    return classes;
  }

  getLabelClasses(): string[] {
    return [BASE_CLASSES.label];
  }

  getHelpClasses(): string[] {
    return this.error
      ? [BASE_CLASSES.help, BASE_CLASSES.helpError]
      : [BASE_CLASSES.help, BASE_CLASSES.helpColor];
  }

  getToggleClasses(): string[] {
    return [BASE_CLASSES.toggle];
  }

  getIconClasses(): string[] {
    return [SIZES[this.size || DEFAULTS.size].icon];
  }

  get inputType(): string {
    return this.visible ? 'text' : 'password';
  }

  get toggleAriaLabel(): string {
    return this.visible ? 'Ocultar contraseña' : 'Mostrar contraseña';
  }
}

export default InputPasswordController;
